import type { EndpointBuilder } from '@reduxjs/toolkit/query'

import { rootApi } from 'root-api-graphql'

import {
  MEETINGS_TAG_TYPES,
  buildMeetingListTag,
  buildMeetingTag,
} from './constants'
import type {
  MeetingsGraphQLResult,
  GraphQLMeetingResult,
} from './types'

export type SupergraphMeetingsInput = any
export type SupergraphMeetingsConnection = MeetingsGraphQLResult
export type SupergraphMeeting = GraphQLMeetingResult

/**
 * Resolve the SuperGraph base URL.
 *
 * In browsers, we prefer window.SUPERGRAPH_BASE_URL (set by webpack define).
 * In tests or node, this may fall back to /api/supergraph/graphql and be proxied
 * by the dev server or cluster ingress.
 */
export const getSupergraphBaseUrl = () => {
  const value = typeof window !== 'undefined'
    && (window as any).SUPERGRAPH_BASE_URL

  return (value && String(value)) || '/api/supergraph/graphql'
}

/** Used as `getMeetingsSupergraph` transformResponse. */
export const transformMeetingsSupergraphConnectionResponse = (response: any) => {
  const conn = response?.meetings ?? {}
  const rawEdges = Array.isArray(conn.edges) ? conn.edges : []
  const edges = rawEdges
    .filter((edge: any) => edge?.node != null)
    .map((edge: any) => ({
      node: {
        ...edge.node,
        key:
          typeof crypto !== 'undefined'
          && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : edge.node.uuid,
      },
    }))

  return {
    meetings: {
      ...conn,
      edges,
    },
  }
}

/** Used as `getMeetingSupergraph` transformResponse. */
export const transformGetMeetingSupergraphResponse = (response: any) => (
  response?.getMeetingById ?? null
)

/** Minimal query for filter-bar total — `filter` includes `search_keyword` like the list query. */
const GetMeetingsCountDocument = `
  query GetMeetingsCount($input: Meetings_MeetingsInput!) {
    meetings(input: $input) {
      total
    }
  }
`

// NOTE:
// - Query field: meetings(input: Meetings_MeetingsInput!)
// - Type names in SuperGraph are prefixed (Meetings_*).
// istanbul ignore next
const GetMeetingsDocument = `
  query GetMeetings($input: Meetings_MeetingsInput!) {
    meetings(input: $input) {
      total
      edges {
        cursor
        node {
          uuid
          kind
          owner_user_uuid
          name
          start_time
          end_time
          location
          location_kind
          cancelled
          status
          is_notetaker_eligible

          meeting_intelligence {
            uuid
            status
            error
          }

          attendees {
            kind
            name
            email
            timezone
            ... on Meetings_InviteeAttendee {
              uuid
              rsvp
              cancelled
            }
            ... on Meetings_HostAttendee {
              uuid
            }
            ... on Meetings_CoHostAttendee {
              uuid
            }
          }

          ... on Meetings_CalendlyMeeting {
            organization_uuid
            co_hosts { uuid }
            event_type {
              uuid
              name
              color
              duration
              kind
              type
              owner_type
              invitees_limit
            }
            invitees_count
            invitees_limit
            filled_count
          }

          ... on Meetings_GoogleMeeting {
            is_organizer
          }

          ... on Meetings_OutlookMeeting {
            is_organizer
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
        hasPreviousPage
      }
    }
  }
`

// For single-meeting detail we can use the additional field defined in supergraph:
//   getMeetingById(id: ID!): Meetings_Meeting
// istanbul ignore next
const GetMeetingDocument = `
  query GetMeeting($id: ID!) {
    getMeetingById(id: $id) {
      uuid
      kind
      owner_user_uuid
      name
      start_time
      end_time
      location
      location_kind
      cancelled
      status
      is_notetaker_eligible

      meeting_intelligence {
        uuid
        status
        error
      }

      attendees {
        kind
        name
        email
        timezone
        ... on Meetings_InviteeAttendee {
          uuid
          rsvp
          cancelled
        }
        ... on Meetings_HostAttendee {
          uuid
        }
        ... on Meetings_CoHostAttendee {
          uuid
        }
      }

      ... on Meetings_CalendlyMeeting {
        organization_uuid
        co_hosts { uuid }
        event_type {
          uuid
          name
          color
          duration
          kind
          type
          owner_type
          invitees_limit
        }
        invitees_count
        invitees_limit
        filled_count
      }

      ... on Meetings_GoogleMeeting {
        is_organizer
      }

      ... on Meetings_OutlookMeeting {
        is_organizer
      }
    }
  }
`

/**
 * SuperGraph (cmd-api + search-api + ci-api) meetings API.
 *
 * This mirrors the existing meetingsGraphqlApi shape so that list/detail code
 * can be swapped behind a feature flag with minimal changes.
 */
export const meetingsSupergraphApi = rootApi
  .enhanceEndpoints({ addTagTypes: MEETINGS_TAG_TYPES })
  .injectEndpoints({
    endpoints: (build: EndpointBuilder<any, any, any>) => ({
      /**
       * List meetings via SuperGraph.
       *
       * Expects a Meetings_MeetingsInput as `input`:
       * {
       *   filter: Meetings_MeetingsFilter
       *   pagination: { first, after, before, last }
       *   orderBy: { start_time: ASC|DESC }
       * }
       */
      getMeetingsSupergraph: build.query<
        SupergraphMeetingsConnection,
        SupergraphMeetingsInput
      >({
        query: (input: SupergraphMeetingsInput) => ({
          document: GetMeetingsDocument,
          variables: { input },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        // Normalize to { meetings: MeetingsConnection } so existing list code
        // (useSearchData + mapNodeToMeeting) can reuse the same shape.
        transformResponse: transformMeetingsSupergraphConnectionResponse,
        providesTags: (result) => {
          const tags = [buildMeetingListTag()]
          const edges = result?.meetings?.edges ?? []
          edges.forEach((edge: any) => {
            if (edge?.node?.uuid) {
              tags.push(buildMeetingTag(edge.node.uuid))
            }
          })
          return tags
        },
      }),

      /**
       * Total count for the meetings filter export / filter bar — matches legacy `getMeetingsFilterCount`.
       */
      getMeetingsSupergraphFilterCount: build.query<
        number,
        SupergraphMeetingsInput
      >({
        query: (input: SupergraphMeetingsInput) => ({
          document: GetMeetingsCountDocument,
          variables: { input },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: (response: any) => response?.meetings?.total ?? 0,
        providesTags: [buildMeetingListTag()],
      }),

      /**
       * Single meeting via SuperGraph.
       *
       * Uses getMeetingById(id: ID!) under the hood.
       */
      getMeetingSupergraph: build.query<
        SupergraphMeeting | null,
        { id: string }
      >({
        query: ({ id }) => ({
          document: GetMeetingDocument,
          variables: { id },
          meta: {
            baseUrl: getSupergraphBaseUrl(),
            fetchAuthToken: true,
          },
        }),
        transformResponse: transformGetMeetingSupergraphResponse,
        providesTags: (result, _error, args) => {
          const tags = [buildMeetingListTag()]
          if (result?.uuid) {
            tags.push(buildMeetingTag(result.uuid))
          } else if (args?.id) {
            tags.push(buildMeetingTag(args.id))
          }
          return tags
        },
        // Suppress global toast for 404 / null cases, like existing getMeeting.
        extraOptions: { handleErrors: false },
      }),
    }),
  })

export const {
  useGetMeetingsSupergraphQuery,
  useGetMeetingsSupergraphFilterCountQuery,
  useLazyGetMeetingsSupergraphFilterCountQuery,
  useLazyGetMeetingsSupergraphQuery,
  useLazyGetMeetingSupergraphQuery,
} = meetingsSupergraphApi
